import { useEffect, useMemo, useState } from "react";
import type { GroupView, Member } from "@/services/types";
import { groupService, memberService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Avatar } from "@/components/base/Avatar";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface BulkAssignModalProps {
  open: boolean;
  group: GroupView;
  onClose: () => void;
  onAssigned: () => void;
}

export function BulkAssignModal({ open, group, onClose, onAssigned }: BulkAssignModalProps) {
  const toast = useToast();
  const [members, setMembers] = useState<Member[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const remainingSlots = group.memberCount - group.tickets.length;

  useEffect(() => {
    if (!open) return;
    let active = true;
    memberService.listMembers().then((m) => active && setMembers(m)).catch(() => {});
    setSelected(new Set());
    setQuery("");
    return () => {
      active = false;
    };
  }, [open]);

  const inGroupIds = useMemo(() => new Set(group.tickets.map((t) => t.memberId)), [group.tickets]);

  const eligible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter(
      (m) =>
        !inGroupIds.has(m.id) &&
        m.status !== "blacklisted" &&
        (!q ||
          m.fullName.toLowerCase().includes(q) ||
          m.memberCode.toLowerCase().includes(q) ||
          m.primaryPhone.includes(q))
    );
  }, [members, query, inGroupIds]);

  const selectedCount = selected.size;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < remainingSlots) next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (selectedCount === 0 || saving) return;
    setSaving(true);
    try {
      await groupService.bulkAssignTickets(group.id, [...selected]);
      toast.success("Tickets assigned", `${selectedCount} member${selectedCount === 1 ? "" : "s"} filled the next available tickets.`);
      onClose();
      onAssigned();
    } catch (err) {
      toast.error("Could not assign members", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk assign members"
      description={`Fill the next ${remainingSlots} available ticket${remainingSlots === 1 ? "" : "s"} with selected members, in order.`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button icon={<i className="ri-user-add-line" />} loading={saving} disabled={selectedCount === 0} onClick={save}>
            Assign {selectedCount > 0 ? `${selectedCount} member${selectedCount === 1 ? "" : "s"}` : ""}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="relative w-full sm:w-80">
          <i className="ri-search-line pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-base text-foreground-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter members…"
            className="h-9 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
          />
          {saving ? (
            <span className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center text-primary-600">
              <i className="ri-loader-4-line animate-spin text-sm" />
            </span>
          ) : query ? (
            <button
              type="button"
              aria-label="Clear filter"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex w-8 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
            >
              <i className="ri-close-line text-base" />
            </button>
          ) : null}
        </div>

        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{eligible.length} eligible member{eligible.length === 1 ? "" : "s"}</span>
          <span className="tnum">{selectedCount} selected · {remainingSlots} slots left</span>
        </div>

        <div className="max-h-80 overflow-auto rounded-md border border-background-200">
          {eligible.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-foreground-500">No eligible members match.</p>
          ) : (
            <ul className="divide-y divide-background-100">
              {eligible.map((m) => {
                const inactive = m.status === "inactive";
                const checked = selected.has(m.id);
                const atCapacity = !checked && selected.size >= remainingSlots;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      disabled={atCapacity}
                      onClick={() => toggle(m.id)}
                      className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left hover:bg-background-100/60 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <input type="checkbox" readOnly checked={checked} className="h-4 w-4 cursor-pointer accent-primary-600" />
                      <Avatar name={m.fullName} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                        <p className="tnum truncate text-xs text-foreground-500">
                          {m.memberCode} · {m.primaryPhone}
                          {m.city ? ` · ${m.city}` : ""}
                          {inactive ? <span className="ml-1 text-warning-600">· inactive</span> : null}
                        </p>
                        {m.enrollments.length > 0 ? (
                          <p className="mt-0.5 text-xs text-secondary-700">
                            In {m.enrollments.length} other group{m.enrollments.length === 1 ? "" : "s"} · outstanding{" "}
                            <CurrencyDisplay amount={m.enrollments.reduce((s, e) => s + e.outstanding, 0)} hideSymbol />
                          </p>
                        ) : null}
                      </div>
                      {inactive ? <Badge variant="inactive" dot>Inactive</Badge> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}